import { Box, Paper, Typography, Link, Stack, Divider } from '@mui/material'
import React, { useRef, useState } from 'react'
import { useForm } from 'react-hook-form';
import * as Yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-hot-toast';
import FormInput from '../inputs/FormInput'
import Btn from '../global/Btn'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { useAuthContext } from '../../auth/useAuthContext'
import { useTranslation } from 'react-i18next';

const FormLogin = ({ onSwitchToOtp }) => {
    const { t } = useTranslation();
    const form = useRef();
    const navigate = useNavigate();

    const NewLoginSchema = Yup.object().shape({
        email: Yup.string().required(t('validation.required')).email(t('validation.emailInvalid')),
        password: Yup.string().required(t('validation.required')),
    });

    const { formState: { errors } } = useForm({
        resolver: yupResolver(NewLoginSchema),
    });

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuthContext();

    const SubmitForm = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setLoading(true);
        try {
            const result = await login(email, password);
            if (result.status === 'success') {
                toast.success(result.message || t('auth.loginSuccess'));
                navigate('/');
            } else {
                toast.error(result.message || t('auth.loginError'));
            }
            // reset(); // Manual reset if needed
        } catch (err) {
            console.error("🚀 ~ SubmitForm ~ Error:", err);
            toast.error(err?.response?.data?.message || t('common.error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper
            elevation={4}
            sx={{
                px: { md: 4, xs: 2 },
                py: { md: 4, xs: 3 },
                borderRadius: 3,
                bgcolor: 'background.paper',
            }}
        >
            <Typography variant="h5" textAlign="center" sx={{ mb: 3, fontWeight: 700 }}>
                {t('auth.signIn')}
            </Typography>

            <Box component={'form'} ref={form} onSubmit={SubmitForm}>
                <Stack spacing={2.5}>
                    <FormInput
                        label={t('auth.email')}
                        type="email"
                        value={email}
                        placeholder="example@example.com"
                        onChange={(e) => setEmail(e.target.value)}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                    />

                    <Box>
                        <FormInput
                            label={t('auth.password')}
                            type="password"
                            placeholder="*********"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            error={!!errors.password}
                            helperText={errors.password?.message}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <Link
                                component={RouterLink}
                                to="/forgot-password"
                                variant="body2"
                                sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                            >
                                {t('auth.forgotPassword')}
                            </Link>
                        </Box>
                    </Box>

                    <Btn type="submit" width="100%" widthMob="100%" sx={{ mx: 'auto' }} loading={loading}>
                        {t('auth.signIn')}
                    </Btn>

                    <Divider>
                        <Typography variant="body2" color="text.secondary">
                            {t('auth.orContinueWith')}
                        </Typography>
                    </Divider>

                    <Btn
                        type="button"
                        variant="outlined"
                        width="100%"
                        widthMob="100%"
                        sx={{ mx: 'auto' }}
                        onClick={onSwitchToOtp}
                    >
                        {t('otp.login_title')}
                    </Btn>

                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Typography variant="body2">
                            {t('auth.dontHaveAccount')}{' '}
                            <Link
                                component={RouterLink}
                                to="/register"
                                sx={{ fontWeight: 600, textDecoration: 'none' }}
                            >
                                {t('auth.signUp')}
                            </Link>
                        </Typography>
                    </Box>
                </Stack>
            </Box>
        </Paper>
    )
}

export default FormLogin